/**
 * Keeps the device-authorization page reachable across a sign-in.
 *
 * Password sign-in renders LoginPage in place, so the URL (and its `?code=`)
 * survives on its own. An SSO sign-in leaves the page for the identity
 * provider and comes back on /auth/callback, so the authorize URL is parked
 * in sessionStorage (same tab, survives the round trip) and taken back by
 * SSOCallbackPage / AuthGate. Also remembers, per code and notice version,
 * that the person already accepted the consent notice on the connect page.
 */

export const AUTHORIZE_PATH = '/task-mining/authorize';
const RETURN_KEY = 'nexus_task_mining_return_to';
const CONSENT_KEY = 'nexus_task_mining_consent';

function storage(): Storage | null {
  try { return window.sessionStorage; } catch { return null; }
}

/** Park the current authorize URL. No-op anywhere else. */
export function parkAuthorizeReturn(loc: Pick<Location, 'pathname' | 'search'> = window.location): void {
  if (loc.pathname.replace(/\/$/, '') !== AUTHORIZE_PATH) return;
  try { storage()?.setItem(RETURN_KEY, loc.pathname + loc.search); } catch { /* storage blocked */ }
}

/** Take (and clear) the parked authorize URL, if any. Only ever returns an authorize path. */
export function takeAuthorizeReturn(): string | null {
  const s = storage();
  if (!s) return null;
  try {
    const v = s.getItem(RETURN_KEY);
    s.removeItem(RETURN_KEY);
    return v && v.startsWith(`${AUTHORIZE_PATH}?`) ? v : null;
  } catch { return null; }
}

export function clearAuthorizeReturn(): void {
  try { storage()?.removeItem(RETURN_KEY); } catch { /* storage blocked */ }
}

export function rememberConsent(userCode: string, version: string): void {
  try { storage()?.setItem(CONSENT_KEY, `${userCode}|${version}`); } catch { /* storage blocked */ }
}

export function hasRememberedConsent(userCode: string, version: string): boolean {
  try { return storage()?.getItem(CONSENT_KEY) === `${userCode}|${version}`; } catch { return false; }
}
